import { Button } from '@ui/button.tsx';
import { useLogout } from '@r/api/auth.tsx';
import { useTranslation } from 'react-i18next';
import { MenuItem } from '@ui/menu-item.tsx';
import { SheetTrigger, SheetContent, Sheet, SheetClose } from '@ui/sheet.tsx';
import { Avatar, AvatarFallback, AvatarImage } from '@ui/avatar.tsx';
import { HelpCircle, LogOut, ShoppingCartIcon, GiftIcon, User } from 'lucide-react';
import { NavigationMenu, NavigationMenuItem, NavigationMenuList } from '@ui/navigation-menu.tsx';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@ui/dropdown-menu.tsx';
import { Link, type LinkProps, useRouter, useLocation, useNavigate } from '@tanstack/react-router';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { CartContext } from '@r/context/cart-context.ts';
import { useContext, useEffect, useMemo, useState } from 'react';
import { cn } from '@r/utils/utils.ts';

import defaultLogo from '@r/assets/images/fiddy/logo.svg';
import { HamburgerMenuIcon } from '@radix-ui/react-icons';
import { LinkProtected } from '@r/components/link-protected/link-protected.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import { useRouteExists } from '@r/hooks/use-route-exists.ts';
import { useUser } from '@r/api/auth.tsx';
import { useBalance } from '@r/context/balance-context.ts';
import { isSquareLogoRatio } from '@r/utils/logo-ratio.ts';
import { WorkspaceContext } from '@r/context/workspace-context.ts';
import { useCoreWorkspaceConfigFlag } from '@r/hooks/use-core-workspace-config-flag.ts';
import { isPurchaseProofFeatureEnabled } from '@client/components/invoices/purchase-proof-feature.ts';

type NavRoute = {
  to: LinkProps['to'];
  labelKey: string;
  hasNotification?: boolean;
  /** true => passe par LinkProtected (garde de route) */
  protected?: boolean;
  /** état actif du bouton desktop */
  isActive?: (pathname: string) => boolean;
};

// Préfixes d'URL de la section « compte » : le menu burger mobile y ajoute les
// liens compte. À garder en phase avec les routes filles de AccountLayout.
const ACCOUNT_PATHS = [
  '/account',
  '/personal-informations',
  '/company-informations',
  '/contact',
  '/orders',
  '/user/invoices',
  '/changelogs'
];

export function MenuHeader() {
  const user = useUser().data;
  const { balance } = useBalance();
  const { cartItemCount } = useContext(CartContext);
  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const { workspace } = useContext(WorkspaceContext);
  const { t } = useTranslation();
  const router = useRouter();
  const navigate = useNavigate();
  const logout = useLogout();
  const location = useLocation();
  const isAccountSection = ACCOUNT_PATHS.some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  );
  const hasShopsRoute = useRouteExists('/shops');
  const hasGamesRoute = useRouteExists('/games');
  const hasOrdersRoute = useRouteExists('/orders');
  const hasCartRoute = useRouteExists('/cart');
  const isOrderShortcutHidden = useCoreWorkspaceConfigFlag('hide-order-shortcut');
  const purchaseProofEnabled = isPurchaseProofFeatureEnabled(workspaceConfigContext);

  // Logo du workspace. Repli si absent ou si l'URL ne charge pas (les URLs S3
  // signées expirent au bout d'une heure) : le nom du programme en wordmark,
  // sinon le logo Fiddy par défaut.
  const configLogo = workspaceConfigContext?.groupedConfigs?.media?.logo;
  const programName =
    workspaceConfigContext?.groupedConfigs?.core?.title ||
    workspaceConfigContext?.groupedConfigs?.core?.name ||
    '';
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const useLogoFallback = !configLogo || logoFailed;
  const showProgramWordmark = useLogoFallback && Boolean(programName);
  const logoSrc = useLogoFallback ? defaultLogo : configLogo;

  // Un logo carré-ish est affiché plus grand (cf. `isSquareLogoRatio`).
  const [logoRatio, setLogoRatio] = useState<number | null>(null);
  const isSquareLogo = isSquareLogoRatio(logoRatio);

  // Nouveau logo → on retente (et on repart d'un ratio non déterminé).
  useEffect(() => {
    setLogoFailed(false);
    setLogoRatio(null);
  }, [configLogo]);

  const { desktopRoutes, mobileRoutes } = useMemo(() => {
    const home: NavRoute = {
      to: '/',
      labelKey: 'menu.header.home',
      isActive: (pathname) => pathname === '/'
    };
    const program: NavRoute = {
      to: '/program',
      labelKey: 'menu.header.program',
      protected: true,
      isActive: (pathname) => pathname === '/program'
    };
    const performance: NavRoute = {
      to: '/performance',
      labelKey: 'menu.header.performances',
      protected: true,
      isActive: (pathname) => pathname === '/performance'
    };
    const shops: NavRoute[] = hasShopsRoute
      ? [
          {
            to: '/shops',
            labelKey: 'menu.header.shops',
            isActive: (pathname) => pathname === '/cart' || pathname.startsWith('/shops')
          }
        ]
      : [];
    const games: NavRoute[] = hasGamesRoute
      ? [
          {
            to: '/games',
            labelKey: 'menu.header.games',
            hasNotification: (workspace?.activeGamesCount ?? 0) > 0,
            isActive: (pathname) => pathname.startsWith('/games')
          }
        ]
      : [];
    const invoices: NavRoute[] = purchaseProofEnabled
      ? [
          {
            to: '/invoices',
            labelKey: 'menu.header.invoices',
            protected: false,
            isActive: (pathname) => pathname === '/invoices'
          }
        ]
      : [];

    return {
      desktopRoutes: [home, program, performance, ...shops, ...games, ...invoices],
      mobileRoutes: [
        home,
        ...shops,
        ...games,
        ...invoices,
        program,
        performance,
        ...(isAccountSection
          ? ([
              { to: '/account', labelKey: 'menu.account.informations' },
              { to: '/personal-informations', labelKey: 'menu.account.personal.informations' },
              { to: '/orders', labelKey: 'menu.account.order.tracking' },
              ...(purchaseProofEnabled
                ? [
                    {
                      to: '/user/invoices',
                      labelKey: 'menu.account.purchase.proofs',
                      protected: false
                    } satisfies NavRoute
                  ]
                : []),
              { to: '/changelogs', labelKey: 'menu.account.program.news' }
            ] satisfies NavRoute[])
          : []),
        { to: '/contact', labelKey: 'menu.account.help' } satisfies NavRoute
      ]
    };
  }, [
    hasShopsRoute,
    hasGamesRoute,
    isAccountSection,
    purchaseProofEnabled,
    workspace?.activeGamesCount
  ]);

  const getRouteLabel = (route: NavRoute) => t(route.labelKey);

  const handleLogout = () => {
    logout();
    router.invalidate().finally(() => {
      navigate({ to: '/login' });
    });
  };

  if (!user) {
    return null;
  }

  // `relative z-10` sur le header : le logo carré déborde sous le header via
  // `translate` , on garantit que tout le header passe au-dessus du contenu.
  return (
    <header className="flex relative z-10 justify-between items-center px-5 h-20 app-shell shrink-0 md:px-7">
      <div
        className={cn(
          'flex h-full justify-center xs:w-6/12 xs:justify-start xl:w-2/12',
          // Logo carré : calé en haut du header puis translaté de l'inset des
          // CTA (translate-y-5 = 20px = (80 − 40) / 2 = le `py-5` du header).
          isSquareLogo ? 'items-start' : 'items-center'
        )}
      >
        <Link to="/" aria-label={t('menu.header.home')} className="flex items-center min-w-0">
          {showProgramWordmark ? (
            <span className="inline-block px-6 py-2 max-w-full text-base font-bold leading-6 truncate rounded-full bg-primary text-foreground nav-btn-shadow">
              {programName}
            </span>
          ) : (
            <img
              src={logoSrc}
              onLoad={(event) => {
                const { naturalWidth, naturalHeight } = event.currentTarget;
                if (naturalWidth > 0 && naturalHeight > 0) {
                  setLogoRatio(naturalWidth / naturalHeight);
                }
              }}
              onError={() => {
                setLogoFailed(true);
                setLogoRatio(null);
              }}
              className={cn(
                'object-contain object-top',
                isSquareLogo ? 'translate-y-5 size-[70px]' : 'h-14 translate-y-2'
              )}
              alt=""
            />
          )}
        </Link>
      </div>

      <nav className="hidden gap-6 justify-center w-8/12 lg:flex">
        <NavigationMenu>
          <NavigationMenuList>
            {desktopRoutes.map((route) => {
              const active = route.isActive?.(location.pathname) ?? false;
              const button = (
                <Button
                  variant="outline"
                  rounded="full"
                  className={cn(
                    'relative border-0 nav-btn-shadow',
                    active && 'bg-foreground text-primary-foreground'
                  )}
                >
                  {getRouteLabel(route)}
                  {route.hasNotification && (
                    <span
                      aria-hidden="true"
                      className="absolute -top-1 -right-1 rounded-full size-4 bg-destructive"
                    />
                  )}
                </Button>
              );

              return (
                <NavigationMenuItem key={route.to}>
                  {route.protected ? (
                    <LinkProtected to={route.to} className="text-base">
                      {button}
                    </LinkProtected>
                  ) : (
                    <Link to={route.to} className="text-base">
                      {button}
                    </Link>
                  )}
                </NavigationMenuItem>
              );
            })}
          </NavigationMenuList>
        </NavigationMenu>
      </nav>

      <div className="flex justify-center w-full xs:w-6/12 xs:justify-end xl:w-2/12">
        <div className="flex items-center space-x-4">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                className="flex justify-center items-center p-0 w-10 rounded-full border-0 aspect-square bg-background nav-btn-shadow text-foreground lg:hidden [&_svg]:size-[18px]"
                variant="outline"
                aria-label={t('menu.header.menu')}
              >
                <HamburgerMenuIcon />
              </Button>
            </SheetTrigger>

            <SheetContent side="left" className="w-screen">
              <SheetClose asChild className="absolute top-6 left-6">
                <Button variant="outline">{t('common.back.btn')}</Button>
              </SheetClose>

              <div className="grid pt-20">
                {mobileRoutes.map((route) => (
                  <SheetClose asChild key={route.to}>
                    {route.protected === false ? (
                      <Link to={route.to}>
                        <MenuItem variant="mobile" className="text-base">
                          {getRouteLabel(route)}
                          {route.hasNotification && (
                            <span
                              aria-hidden="true"
                              className="self-center ml-2 rounded-full size-3 bg-destructive"
                            />
                          )}
                        </MenuItem>
                      </Link>
                    ) : (
                      <LinkProtected to={route.to}>
                        <MenuItem variant="mobile" className="text-base">
                          {getRouteLabel(route)}
                          {route.hasNotification && (
                            <span
                              aria-hidden="true"
                              className="self-center ml-2 rounded-full size-3 bg-destructive"
                            />
                          )}
                        </MenuItem>
                      </LinkProtected>
                    )}
                  </SheetClose>
                ))}
              </div>
            </SheetContent>
          </Sheet>

          <div className="hidden text-end lg:block">
            <div className="mb-0.5 text-base text-foreground">
              {`${user.firstname} ${user.lastname}`}
            </div>

            <div id="menu-header-points-amount" className="text-xs text-muted-foreground">
              {pointsTranslation(t, balance, true)}
            </div>
          </div>

          {/* `modal={false}` : pas de verrou de scroll Radix → n'ajoute pas de
              padding au body → ne décale pas le contenu centré à l'ouverture.
              État contrôlé : on ferme à la main au clic sur un lien (la nav SPA
              ne démonte pas le menu toute seule). */}
          <DropdownMenu modal={false} open={userMenuOpen} onOpenChange={setUserMenuOpen}>
            <DropdownMenuTrigger className="focus-visible:outline-none">
              <Avatar size="nav" className="bg-background nav-btn-shadow">
                {user.avatar ? (
                  <AvatarImage src={user.avatar} />
                ) : (
                  <AvatarFallback
                    text={`${user.firstname} ${user.lastname}`}
                    fontSize="nav"
                    initials={true}
                    className="bg-background text-foreground"
                  />
                )}
              </Avatar>
            </DropdownMenuTrigger>

            <DropdownMenuContent className="p-5 mx-5 my-3 rounded-3xl md:mx-7">
              <div className="grow">
                <LinkProtected to="/account">
                  <MenuItem onClick={() => setUserMenuOpen(false)}>
                    <User />
                    {t('menu.header.user.my.account')}
                  </MenuItem>
                </LinkProtected>

                <LinkProtected to="/contact">
                  <MenuItem onClick={() => setUserMenuOpen(false)}>
                    <HelpCircle />
                    {t('menu.header.user.help')}
                  </MenuItem>
                </LinkProtected>

                <MenuItem
                  onClick={() => {
                    setUserMenuOpen(false);
                    handleLogout();
                  }}
                >
                  <LogOut />
                  {t('menu.header.user.logout')}
                </MenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {hasOrdersRoute && !isOrderShortcutHidden && (
            <Link
              to="/orders"
              aria-label={t('menu.account.order.tracking')}
              title={t('menu.account.order.tracking')}
              className="flex justify-center items-center w-10 rounded-full aspect-square bg-background nav-btn-shadow text-foreground"
            >
              <GiftIcon size="18" />
            </Link>
          )}

          {hasCartRoute && cartItemCount > 0 && (
            <Link
              to="/cart"
              className="flex relative justify-center items-center w-10 rounded-full aspect-square bg-background nav-btn-shadow text-foreground"
            >
              <ShoppingCartIcon size="18" />
              <span className="flex absolute -top-1 -right-1 justify-center items-center w-5 h-5 text-xs rounded-full bg-primary text-primary-foreground">
                {cartItemCount}
              </span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
